// Saphir order form: offers, pieces, add-on, validation, sending, thank-you card and sticky button.
// Markup and texts come from blocks/saphir-order-form.liquid; prices follow saphir-form-core.js.
import { offerTotals, normalizePhone, validateCustomer, whatsappText, styleKey, isTestOrder, newEventId, attributionParams, totalNote } from '@saphir/form-core';
import { buildContents, eventParams, track as trackMeta } from '@saphir/meta';
import { StandardEvents } from '@shopify/events';

const ATTR_COOKIE = 'saphir_attr';
const DAY = 24 * 60 * 60;
const REQUEST_TIMEOUT_MS = 15000;
const RETRY_DELAY_MS = 1200;
// The server sends its own Purchase (same event id) to Meta while answering; the browser's copy waits
// so the server's richer one arrives first and is the one Meta keeps.
const PURCHASE_DELAY_MS = 1000;

const dh = (cents) => Math.round(cents / 100);
// Texts from Liquid carry __name__ placeholders (Liquid output tags cannot hold curly braces).
const fill = (text, values) => text.replace(/__(\w+)__/g, (_, key) => String(values[key] ?? ''));
const gid = (type, id) => `gid://shopify/${type}/${id}`;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

function readCookie(name) {
  const hit = document.cookie.split('; ').find((c) => c.startsWith(`${name}=`));
  return hit ? decodeURIComponent(hit.slice(name.length + 1)) : '';
}

function writeCookie(name, value, maxAge) {
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${maxAge}; Path=/; SameSite=Lax`;
}

// First-touch attribution for 30 days, plus Meta's _fbc from fbclid (spec 2026-10-24 §7.1).
function captureAttribution() {
  const found = attributionParams(window.location.search);
  if (Object.keys(found).length && !readCookie(ATTR_COOKIE)) {
    writeCookie(ATTR_COOKIE, JSON.stringify({ ...found, landing_url: window.location.href.slice(0, 500) }), 30 * DAY);
  }
  if (found.fbclid && !readCookie('_fbc')) writeCookie('_fbc', `fb.1.${Date.now()}.${found.fbclid}`, 90 * DAY);
}

function tracking() {
  let saved = {};
  try {
    saved = JSON.parse(readCookie(ATTR_COOKIE) || '{}');
  } catch {
    saved = {};
  }
  const out = { ...saved, page_url: window.location.href.slice(0, 500) };
  const fbp = readCookie('_fbp');
  const fbc = readCookie('_fbc');
  if (fbp) out.fbp = fbp;
  if (fbc) out.fbc = fbc;
  return out;
}

class SaphirForm extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  #observer = null;
  #sticky = null;
  #state = { offer: 0, pieces: [], addon: false, sending: false, eventId: '', checkoutTracked: false };

  connectedCallback() {
    this.#abort = new AbortController();
    try {
      this.#init();
    } catch (error) {
      console.error('[saphir-form] init failed', error);
    }
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    this.#sticky?.remove();
  }

  #on(target, type, handler) {
    target?.addEventListener(type, handler, { signal: this.#abort.signal });
  }

  #init() {
    this.product = JSON.parse(this.querySelector('[data-sf-product]').textContent);
    this.config = JSON.parse(this.querySelector('[data-sf-config]').textContent);
    this.strings = this.config.strings;
    this.card = this.querySelector('[data-sf-card]');
    this.form = this.querySelector('[data-sf-form]');
    this.offers = [...this.querySelectorAll('[data-sf-offer]')];
    this.variants = new Map(this.product.variants.map((v) => [v.id, v]));
    this.hasOptions = this.product.variants.length > 1;

    const first = this.variants.get(this.product.selected) ?? this.product.variants.find((v) => v.available) ?? this.product.variants[0];
    this.#state.pieces = [first.id];
    const preset = this.offers.findIndex((el) => el.dataset.default === 'true');
    this.#state.offer = preset >= 0 ? preset : 0;

    this.#bindOffers();
    this.#bindAddon();
    this.#bindFields();
    this.#bindPicker();
    this.#bindWhatsapp();
    this.#bindSticky();
    this.#bindStylePreview();
    this.#selectOffer(this.#state.offer, { focus: false });
  }

  // ---------- Offers and pieces ----------

  #bindOffers() {
    this.offers.forEach((el, index) => {
      const button = el.querySelector('[data-sf-offer-button]');
      this.#on(button, 'click', () => this.#selectOffer(index, { focus: false }));
      this.#on(button, 'keydown', (event) => {
        const step = { ArrowDown: 1, ArrowRight: 1, ArrowUp: -1, ArrowLeft: -1 }[event.key];
        if (!step) return;
        event.preventDefault();
        this.#selectOffer((index + step + this.offers.length) % this.offers.length, { focus: true });
      });
    });
  }

  #qty() {
    return Number(this.offers[this.#state.offer]?.dataset.qty) || 1;
  }

  #selectOffer(index, { focus }) {
    this.#state.offer = index;
    const qty = this.#qty();
    const pieces = this.#state.pieces;
    while (pieces.length < qty) pieces.push(pieces[0]);
    pieces.length = qty;

    this.offers.forEach((el, i) => {
      const on = i === index;
      const button = el.querySelector('[data-sf-offer-button]');
      el.dataset.on = String(on);
      button.setAttribute('aria-checked', String(on));
      button.tabIndex = on ? 0 : -1;
      const box = el.querySelector('[data-sf-pieces]');
      if (!on || !this.hasOptions) {
        box.hidden = true;
        box.replaceChildren();
      }
    });
    this.#renderPieces();
    this.#update();
    if (focus) this.offers[index].querySelector('[data-sf-offer-button]').focus();
  }

  // Every offer, the 1-piece one included, picks its sizes/colours inside the selected offer (the theme's own picker is hidden).
  #renderPieces() {
    const box = this.offers[this.#state.offer]?.querySelector('[data-sf-pieces]');
    if (!box || !this.hasOptions) return;
    box.hidden = false;
    box.style.setProperty('--sf-options', String(this.product.options.length));
    // The rows are rebuilt, so the focused dropdown is found again by its row and position.
    const active = document.activeElement;
    let spot = null;
    if (active instanceof HTMLSelectElement && box.contains(active)) {
      const row = active.closest('.sf-piece');
      spot = { row: [...box.children].indexOf(row), select: [...row.querySelectorAll('select')].indexOf(active) };
    }
    box.replaceChildren(...this.#state.pieces.map((id, i) => this.#pieceRow(i, this.variants.get(id))));
    if (spot) box.children[spot.row]?.querySelectorAll('select')[spot.select]?.focus();
  }

  #pieceRow(index, variant) {
    const row = document.createElement('div');
    row.className = 'sf-piece';
    const label = document.createElement('span');
    label.textContent = fill(this.strings.piece, { number: index + 1 });
    row.append(label);

    this.product.options.forEach((name, k) => {
      const select = document.createElement('select');
      select.className = 'sf-in sf-in--sm';
      select.setAttribute('aria-label', `${label.textContent}، ${name}`);
      const values = [...new Set(this.product.variants.map((v) => v.options[k]))];
      for (const value of values) {
        const wanted = variant.options.map((o, j) => (j === k ? value : o));
        const match = this.product.variants.find((v) => v.options.every((o, j) => o === wanted[j]));
        const option = new Option(match?.available ? value : `${value} (${this.strings.soldOut})`, value);
        option.disabled = !match?.available;
        option.selected = value === variant.options[k];
        select.append(option);
      }
      this.#on(select, 'change', () => this.#changePiece(index, k, select.value));
      row.append(select);
    });
    return row;
  }

  #changePiece(index, optionIndex, value) {
    const current = this.variants.get(this.#state.pieces[index]);
    const wanted = current.options.map((o, j) => (j === optionIndex ? value : o));
    const exact = this.product.variants.find((v) => v.available && v.options.every((o, j) => o === wanted[j]));
    const near = this.product.variants.find((v) => v.available && v.options[optionIndex] === value);
    const next = exact ?? near ?? current;
    this.#state.pieces[index] = next.id;
    this.#renderPieces();
    this.#update();
  }

  // Piece 1 follows the theme's own colour/size picker above the form.
  #bindPicker() {
    const section = this.closest('.shopify-section') ?? document;
    this.#on(section, StandardEvents.productSelect, async (event) => {
      if (event.target instanceof Element && event.target.closest('product-card')) return;
      const names = this.product.options;
      const chosen = Array.isArray(event.selectedOptions) ? event.selectedOptions : [];
      let match = null;
      if (chosen.length === names.length) {
        const values = names.map((name) => chosen.find((o) => o.name === name)?.value);
        match = this.product.variants.find((v) => v.options.every((o, j) => o === values[j]));
      }
      if (!match && event.promise) {
        try {
          const result = await event.promise;
          match = this.variants.get(Number(result?.variant?.id)) ?? null;
        } catch {
          match = null;
        }
      }
      if (!match) return;
      this.#state.pieces[0] = match.id;
      this.#renderPieces();
      this.#update();
    });
  }

  // ---------- Add-on ----------

  #bindAddon() {
    this.addonBox = this.querySelector('[data-sf-addon]');
    if (!this.addonBox) return;
    const check = this.addonBox.querySelector('[data-sf-addon-check]');
    const variantSelect = this.addonBox.querySelector('[data-sf-addon-variant]');
    this.#on(check, 'change', () => {
      this.#state.addon = check.checked;
      this.addonBox.dataset.on = String(check.checked);
      if (variantSelect) variantSelect.hidden = !check.checked;
      this.#update();
    });
  }

  #addonVariantId() {
    const select = this.addonBox?.querySelector('[data-sf-addon-variant]');
    return Number(select?.value || this.config.addon?.variant);
  }

  // ---------- Prices ----------

  #offerPrices(el, index) {
    const qty = Number(el.dataset.qty) || 1;
    if (index === this.#state.offer) return this.#state.pieces.map((id) => this.variants.get(id).price);
    const one = this.variants.get(this.#state.pieces[0]).price;
    return Array.from({ length: qty }, () => one);
  }

  #totals() {
    const el = this.offers[this.#state.offer];
    const offer = { type: el?.dataset.type ?? 'none', value: Number(el?.dataset.value) || 0 };
    const totals = offerTotals(this.#offerPrices(el, this.#state.offer), offer);
    const addonCents = this.#state.addon && this.config.addon ? this.config.addon.priceCents : 0;
    return { ...totals, addonCents, orderCents: totals.totalCents + addonCents };
  }

  #update() {
    const s = this.strings;
    this.offers.forEach((el, i) => {
      const qty = Number(el.dataset.qty) || 1;
      const t = offerTotals(this.#offerPrices(el, i), { type: el.dataset.type, value: Number(el.dataset.value) || 0 });
      el.querySelector('[data-sf-price]').textContent = fill(s.price, { amount: dh(t.totalCents) });
      const base = el.querySelector('[data-sf-base]');
      base.textContent = fill(s.price, { amount: dh(t.baseCents) });
      base.hidden = t.saveCents <= 0;
      const save = el.querySelector('[data-sf-save]');
      save.textContent = fill(s.save, { amount: dh(t.saveCents) });
      save.hidden = t.saveCents <= 0;
      // The merchant's own text under the title (data-sf-fixed) is left as it is.
      const per = el.querySelector('[data-sf-per]');
      if (!per.hasAttribute('data-sf-fixed')) per.textContent = qty === 1 ? s.freeDelivery : fill(s.perPiece, { amount: dh(t.perPieceCents) });
    });

    const t = this.#totals();
    const set = (selector, text) => {
      const node = this.querySelector(selector);
      if (node) node.textContent = text;
    };
    const total = fill(s.price, { amount: dh(t.orderCents) });
    set('[data-sf-sum-total]', total);
    set('[data-sf-submit-total]', total);
    // Under the total: the chosen offer's own delivery text (offer block setting, "free" by default), the saving, the add-on.
    set(
      '[data-sf-total-note]',
      totalNote({
        deliveryLabel: s.summaryDelivery,
        deliveryText: this.offers[this.#state.offer]?.dataset.delivery || '',
        free: s.summaryFree,
        saveCents: t.saveCents,
        saveText: fill(s.save, { amount: dh(t.saveCents) }),
        addonTitle: this.#state.addon && this.config.addon ? this.config.addon.title : null,
      }),
    );
    if (this.#sticky) {
      const small = this.#sticky.querySelector('[data-sf-sticky-total]');
      if (small) small.textContent = fill(s.stickyTotal, { amount: dh(t.orderCents) });
    }
  }

  // ---------- Fields and sending ----------

  #field(name) {
    return /** @type {HTMLInputElement | null} */ (this.form.elements.namedItem(name));
  }

  #customer() {
    const value = (name) => this.#field(name)?.value.trim() ?? '';
    return { name: value('name'), phone: value('phone'), city: value('city'), address: value('address'), note: value('note') };
  }

  #showError(name, code) {
    const input = this.#field(name);
    const message = this.querySelector(`[data-sf-error="${name}"]`);
    if (!input || !message) return false;
    const text = this.strings.errors[`${name}_${code}`] ?? this.strings.errors[`${name}_invalid`];
    message.textContent = text ?? '';
    message.hidden = !text;
    input.setAttribute('aria-invalid', 'true');
    input.setAttribute('aria-describedby', message.id);
    return true;
  }

  #clearError(name) {
    const input = this.#field(name);
    const message = this.querySelector(`[data-sf-error="${name}"]`);
    input?.removeAttribute('aria-invalid');
    input?.removeAttribute('aria-describedby');
    if (message) message.hidden = true;
  }

  // Brings the first wrong field on screen without focusing it: focus would open the keyboard and zoom the page on phones.
  #reveal(name) {
    const input = this.#field(name);
    (input?.closest('.sf-field') ?? input)?.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'nearest' });
  }

  #alert(text) {
    const box = this.querySelector('[data-sf-alert]');
    if (!box) return;
    box.textContent = text ?? '';
    box.hidden = !text;
  }

  #bindFields() {
    this.#on(this.form, 'input', (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.getAttribute('aria-invalid') !== 'true') return;
      const errors = validateCustomer(this.#customer(), { addressRequired: this.config.addressRequired });
      if (!errors[input.name]) this.#clearError(input.name);
    });
    this.#on(this.form, 'submit', (event) => {
      event.preventDefault();
      this.#submit();
    });
    // Meta's InitiateCheckout: the first time the customer enters a field (spec 2026-10-24 §7.2).
    this.#on(this.form, 'focusin', (event) => {
      if (event.target instanceof Element && event.target.matches('input, select, textarea')) this.#trackCheckout();
    });
  }

  // ---------- Meta Pixel ----------

  #metaParams(addonIncluded) {
    const t = this.#totals();
    const perPieceDh = dh(t.perPieceCents);
    const pieces = this.#state.pieces.map((id) => ({ variantId: id, priceDh: perPieceDh }));
    const addon =
      addonIncluded && this.config.addon
        ? { productId: this.config.addon.product, variantId: this.#addonVariantId(), priceDh: dh(this.config.addon.priceCents) }
        : null;
    const totalDh = dh(t.totalCents) + (addon ? addon.priceDh : 0);
    return eventParams({ contents: buildContents(this.product.id, pieces, addon), totalDh, title: this.product.title });
  }

  #trackCheckout() {
    if (this.#state.checkoutTracked) return;
    this.#state.checkoutTracked = true;
    trackMeta('InitiateCheckout', this.#metaParams(this.#state.addon));
  }

  async #submit() {
    if (this.#state.sending) return;
    this.#alert('');
    const customer = this.#customer();
    const errors = validateCustomer(customer, { addressRequired: this.config.addressRequired });
    ['name', 'phone', 'city', 'address', 'note'].forEach((name) => this.#clearError(name));
    const wrong = Object.keys(errors);
    if (wrong.length) {
      wrong.forEach((name) => this.#showError(name, errors[name]));
      this.#reveal(wrong[0]);
      return;
    }
    if (this.#state.pieces.some((id) => !this.variants.get(id)?.available)) {
      this.#alert(this.strings.errors.items_unavailable);
      return;
    }

    const endpoint = (this.dataset.endpoint || '').replace(/\/+$/, '');
    if (!endpoint) {
      if (this.dataset.designMode === 'true') this.#thanks(customer, null, this.#totals().orderCents, this.#state.addon);
      else this.#fallback();
      return;
    }

    this.#state.eventId ||= newEventId();
    this.#setSending(true);
    const body = this.#requestBody(customer);
    let outcome = await this.#post(endpoint, body);
    if (outcome.retry) {
      await sleep(RETRY_DELAY_MS);
      outcome = await this.#post(endpoint, body);
    }
    this.#setSending(false);

    if (outcome.ok) {
      // The server says whether the add-on is really in the order (null: keep what the page showed).
      const addon = typeof outcome.data.addon === 'boolean' ? outcome.data.addon : this.#state.addon;
      const totalCents = Math.round((outcome.data.total ?? dh(this.#totals().orderCents)) * 100);
      // Same event id as the server's Conversions API Purchase, so Meta counts the order once.
      const eventId = this.#state.eventId;
      if (outcome.data.orderName) setTimeout(() => trackMeta('Purchase', { ...this.#metaParams(addon), value: dh(totalCents) }, eventId), PURCHASE_DELAY_MS);
      this.#thanks(customer, outcome.data.orderName, totalCents, addon);
      this.#state.eventId = '';
    } else if (outcome.errors) {
      const fields = Object.keys(outcome.errors).filter((name) => this.#showError(name, outcome.errors[name]));
      if (outcome.errors.items) this.#alert(this.strings.errors.items_unavailable);
      if (fields.length) this.#reveal(fields[0]);
      else if (!outcome.errors.items) this.#fallback();
    } else {
      this.#fallback();
    }
  }

  #requestBody(customer) {
    return {
      productId: gid('Product', this.product.id),
      offerIndex: this.#state.offer,
      items: this.#state.pieces.map((id) => ({ variantId: gid('ProductVariant', id) })),
      upsell: this.#state.addon && this.config.addon ? { variantId: gid('ProductVariant', this.#addonVariantId()) } : null,
      customer,
      locale: this.config.locale === 'fr' ? 'fr' : 'ar',
      themeId: window.Shopify?.theme?.id ? String(window.Shopify.theme.id) : null,
      tracking: tracking(),
      eventId: this.#state.eventId,
      hp: this.#field('hp')?.value ?? '',
      test: isTestOrder(window.location.search, this.dataset.designMode === 'true'),
    };
  }

  async #post(endpoint, body) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      const response = await fetch(`${endpoint}/api/order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok && data.ok) return { ok: true, data };
      if (response.status === 422 && data.errors) return { ok: false, errors: data.errors };
      return { ok: false, retry: response.status === 429 || response.status >= 500 || data.retry === true };
    } catch {
      return { ok: false, retry: true };
    } finally {
      clearTimeout(timer);
    }
  }

  #setSending(on) {
    this.#state.sending = on;
    const button = this.querySelector('[data-sf-submit]');
    const label = this.querySelector('[data-sf-submit-label]');
    if (!button || !label) return;
    button.disabled = on;
    label.dataset.idle ??= label.textContent;
    label.textContent = on ? this.strings.sending : label.dataset.idle;
  }

  // ---------- WhatsApp ----------

  #pieceText(id) {
    return this.variants.get(id).options.join('، ');
  }

  #whatsappUrl(customer = this.#customer()) {
    if (!this.config.whatsapp) return null;
    const offer = this.offers[this.#state.offer];
    const text = whatsappText({
      product: this.product.title,
      offerTitle: offer?.dataset.title ?? '',
      pieces: this.hasOptions ? this.#state.pieces.map((id) => this.#pieceText(id)) : [],
      addon: this.#state.addon ? this.config.addon?.title ?? null : null,
      name: customer.name,
      phone: normalizePhone(customer.phone) ?? customer.phone,
      city: customer.city,
      address: customer.address,
      totalDh: dh(this.#totals().orderCents),
    });
    return `https://wa.me/${this.config.whatsapp}?text=${encodeURIComponent(text)}`;
  }

  #bindWhatsapp() {
    const link = this.querySelector('[data-sf-wa]');
    this.#on(link, 'click', () => {
      const url = this.#whatsappUrl();
      if (url) link.href = url;
    });
  }

  #fallback() {
    const link = this.querySelector('[data-sf-wa]');
    const url = this.#whatsappUrl();
    if (link && url) {
      link.href = url;
      this.#alert(this.strings.errors.network);
      link.focus();
    } else {
      this.#alert(this.strings.errors.network_no_whatsapp);
    }
  }

  // ---------- Thank-you card ----------

  #thanks(customer, orderName, totalCents, addonIncluded) {
    const template = this.querySelector('[data-sf-thanks-template]');
    if (!template) return;
    const s = this.strings;
    const view = template.content.cloneNode(true);
    view.querySelector('[data-slot="title"]').textContent = fill(s.thanksTitle, { name: customer.name.split(/\s+/)[0] });
    const list = view.querySelector('[data-slot="summary"]');
    const row = (term, value, className) => {
      const div = document.createElement('div');
      if (className) div.className = className;
      const dt = document.createElement('dt');
      const dd = document.createElement('dd');
      dt.textContent = term;
      dd.textContent = value;
      div.append(dt, dd);
      list.append(div);
      return dd;
    };
    row(s.summaryOrder, `${this.product.title}، ${this.offers[this.#state.offer]?.dataset.title ?? ''}`);
    if (this.hasOptions) this.#state.pieces.forEach((id, i) => row(fill(s.piece, { number: i + 1 }), this.#pieceText(id)));
    if (addonIncluded && this.config.addon) row(s.summaryAddon, this.config.addon.title);
    row(s.summaryCity, customer.city);
    if (orderName) row(s.orderNumber, orderName).setAttribute('dir', 'ltr');
    row(s.summaryTotal, fill(s.price, { amount: dh(totalCents) }), 'sf-total');

    this.card.replaceChildren(view);
    if (this.#sticky) this.#sticky.hidden = true;
    this.#observer?.disconnect();
    this.card.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
    this.card.querySelector('[data-sf-thanks]')?.focus({ preventScroll: true });
  }

  // ---------- Sticky button (phones) ----------

  #bindSticky() {
    const sticky = this.querySelector('[data-sf-sticky]');
    if (!sticky || !('IntersectionObserver' in window)) return;
    // Moved to <body> so no transformed ancestor can trap the fixed position.
    document.body.append(sticky);
    this.#sticky = sticky;
    this.#on(sticky.querySelector('[data-sf-sticky-button]'), 'click', () => {
      this.card.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
      setTimeout(() => this.#field('name')?.focus({ preventScroll: true }), reduceMotion() ? 0 : 500);
    });
    // Shown whenever the form is off screen, above or below (reading the description after the form included).
    this.#observer = new IntersectionObserver(([entry]) => {
      sticky.hidden = entry.isIntersecting;
    });
    this.#observer.observe(this.card);
  }

  // ---------- Style preview (theme editor bar, or ?saphir_style= for testing) ----------

  #applyStyle(key) {
    this.card.dataset.sfStyle = key;
    if (this.#sticky) this.#sticky.dataset.sfStyle = key;
    this.querySelectorAll('[data-sf-preview]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.sfPreview === key)));
  }

  #bindStylePreview() {
    const asked = new URL(window.location.href).searchParams.get('saphir_style');
    if (asked) this.#applyStyle(styleKey(asked, this.card.dataset.sfStyle));
    this.querySelectorAll('[data-sf-preview]').forEach((button) => {
      this.#on(button, 'click', () => this.#applyStyle(button.dataset.sfPreview));
    });
  }
}

captureAttribution();
if (!customElements.get('saphir-form')) customElements.define('saphir-form', SaphirForm);
